import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Search,
  Filter,
  X,
  Users,
  Euro,
  ShoppingCart,
  MapPin,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp,
  Download,
} from 'lucide-react'
import { advancedSearch, AdvancedSearchFilters, AdvancedSearchResult } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/Toast'
import CustomerDetailModal from './CustomerDetailModal'

// ============================================================================
// CONSTANTES
// ============================================================================

const GROUPS = [
  { id: 3, label: 'Cliente PVP' },
  { id: 4, label: 'Distribuidor' },
  { id: 6, label: 'VIP' },
]

const RFM_SEGMENTS = [
  'Champions (VIP)',
  'Clientes Leales',
  'Prometedores',
  'Clientes Nuevos',
  'Hibernando',
  'En Riesgo',
  'Bajando',
  'Perdidos',
  'One-Timers',
]

const COUNTRIES = [
  'España',
  'Portugal',
  'Francia',
  'Italia',
  'Alemania',
  'Reino Unido',
  'Bélgica',
  'Países Bajos',
]

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

function FilterSection({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
}: {
  title: string
  icon: any
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4 last:border-0 last:pb-0 last:mb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          <Icon className="w-4 h-4" />
          {title}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && children}
    </div>
  )
}

function ResultRow({
  customer,
  onClick,
}: {
  customer: AdvancedSearchResult
  onClick: () => void
}) {
  return (
    <tr
      className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900 dark:text-white">{customer.name}</div>
        <div className="text-xs text-slate-500">{customer.email}</div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {customer.company || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {customer.country || '-'}
      </td>
      <td className="px-4 py-3">
        <span className={cn(
          'px-2 py-0.5 text-xs font-medium rounded-full',
          customer.group_id === 6 ? 'bg-amber-100 text-amber-700' :
          customer.group_id === 4 ? 'bg-purple-100 text-purple-700' :
          'bg-blue-100 text-blue-700'
        )}>
          {customer.group_name}
        </span>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white text-center">
        {customer.total_orders}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white text-right">
        {customer.total_revenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 text-right">
        {customer.avg_order_value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {customer.days_since_last_order !== null ? `${customer.days_since_last_order}d` : 'Nunca'}
      </td>
      <td className="px-4 py-3">
        {customer.rfm_segment && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
            {customer.rfm_segment}
          </span>
        )}
      </td>
    </tr>
  )
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function AdvancedSearchPanel() {
  const toast = useToast()
  const [filters, setFilters] = useState<AdvancedSearchFilters>({
    active: true,
    limit: 100,
  })
  const [results, setResults] = useState<AdvancedSearchResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState<string | null>(null)

  const searchMutation = useMutation({
    mutationFn: () => advancedSearch.search(filters),
    onSuccess: (data) => {
      setResults(data.customers)
      setHasSearched(true)
      if (data.customers.length === 0) {
        toast.info('Sin resultados', 'No se encontraron clientes con los filtros aplicados')
      }
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const handleSearch = () => {
    searchMutation.mutate()
  }

  const handleClearFilters = () => {
    setFilters({ active: true, limit: 100 })
    setResults([])
    setHasSearched(false)
  }

  const handleExportCSV = () => {
    if (results.length === 0) return

    const headers = ['Email', 'Nombre', 'Empresa', 'País', 'Grupo', 'Pedidos', 'Facturación', 'Ticket Medio', 'Días Sin Comprar', 'Segmento RFM']
    const rows = results.map(c => [
      c.email,
      c.name,
      c.company || '',
      c.country || '',
      c.group_name,
      c.total_orders,
      c.total_revenue.toFixed(2),
      c.avg_order_value.toFixed(2),
      c.days_since_last_order ?? '',
      c.rfm_segment || ''
    ])

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `clientes_busqueda_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)

    toast.success('Exportado', `${results.length} clientes exportados a CSV`)
  }

  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'limit') return false
    if (key === 'active' && value === true) return false
    if (value === undefined || value === null) return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  }).length

  return (
    <div className="flex gap-6">
      {/* Filtros */}
      <div className="w-80 flex-shrink-0">
        <div className="card p-4 sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filtros
              {activeFiltersCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  {activeFiltersCount}
                </span>
              )}
            </h3>
            {activeFiltersCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Limpiar
              </button>
            )}
          </div>

          <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Grupo */}
            <FilterSection title="Grupo de cliente" icon={Users}>
              <div className="space-y-2">
                {GROUPS.map((group) => (
                  <label key={group.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.group_ids?.includes(group.id) || false}
                      onChange={(e) => {
                        const current = filters.group_ids || []
                        setFilters({
                          ...filters,
                          group_ids: e.target.checked
                            ? [...current, group.id]
                            : current.filter((id) => id !== group.id),
                        })
                      }}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{group.label}</span>
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Facturación */}
            <FilterSection title="Facturación" icon={Euro}>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Mínimo (€)</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.min_revenue || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      min_revenue: e.target.value ? Number(e.target.value) : undefined
                    })}
                    className="input w-full text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Máximo (€)</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.max_revenue || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      max_revenue: e.target.value ? Number(e.target.value) : undefined
                    })}
                    className="input w-full text-sm"
                    placeholder="Sin límite"
                  />
                </div>
              </div>
            </FilterSection>

            {/* Pedidos */}
            <FilterSection title="Número de pedidos" icon={ShoppingCart}>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Mínimo</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.min_orders || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      min_orders: e.target.value ? Number(e.target.value) : undefined
                    })}
                    className="input w-full text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Máximo</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.max_orders || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      max_orders: e.target.value ? Number(e.target.value) : undefined
                    })}
                    className="input w-full text-sm"
                    placeholder="Sin límite"
                  />
                </div>
              </div>
            </FilterSection>

            {/* Último pedido */}
            <FilterSection title="Días sin comprar" icon={Calendar}>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Mínimo días</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.last_order_days_min || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      last_order_days_min: e.target.value ? Number(e.target.value) : undefined
                    })}
                    className="input w-full text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Máximo días</label>
                  <input
                    type="number"
                    min="0"
                    value={filters.last_order_days_max || ''}
                    onChange={(e) => setFilters({
                      ...filters,
                      last_order_days_max: e.target.value ? Number(e.target.value) : undefined
                    })}
                    className="input w-full text-sm"
                    placeholder="Sin límite"
                  />
                </div>
              </div>
            </FilterSection>

            {/* País */}
            <FilterSection title="País" icon={MapPin} defaultOpen={false}>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {COUNTRIES.map((country) => (
                  <label key={country} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.countries?.includes(country) || false}
                      onChange={(e) => {
                        const current = filters.countries || []
                        setFilters({
                          ...filters,
                          countries: e.target.checked
                            ? [...current, country]
                            : current.filter((c) => c !== country),
                        })
                      }}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{country}</span>
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Segmento RFM */}
            <FilterSection title="Segmento RFM" icon={Tag} defaultOpen={false}>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {RFM_SEGMENTS.map((segment) => (
                  <label key={segment} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.rfm_segments?.includes(segment) || false}
                      onChange={(e) => {
                        const current = filters.rfm_segments || []
                        setFilters({
                          ...filters,
                          rfm_segments: e.target.checked
                            ? [...current, segment]
                            : current.filter((s) => s !== segment),
                        })
                      }}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{segment}</span>
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Estado */}
            <FilterSection title="Estado" icon={Users} defaultOpen={false}>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.active === true}
                    onChange={(e) => setFilters({
                      ...filters,
                      active: e.target.checked ? true : undefined
                    })}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Solo activos</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.newsletter === true}
                    onChange={(e) => setFilters({
                      ...filters,
                      newsletter: e.target.checked ? true : undefined
                    })}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Con newsletter</span>
                </label>
              </div>
            </FilterSection>

            {/* Límite */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <label className="block text-xs text-slate-500 mb-1">Máximo resultados</label>
              <select
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value) })}
                className="input w-full text-sm"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleSearch}
            disabled={searchMutation.isPending}
            className="btn btn-primary w-full mt-4"
          >
            <Search className={cn('w-4 h-4 mr-2', searchMutation.isPending && 'animate-spin')} />
            {searchMutation.isPending ? 'Buscando...' : 'Buscar Clientes'}
          </button>
        </div>
      </div>

      {/* Resultados */}
      <div className="flex-1 min-w-0">
        {!hasSearched ? (
          <div className="card p-12 text-center">
            <Search className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              Búsqueda Avanzada de Clientes
            </h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Utiliza los filtros de la izquierda para encontrar clientes por facturación,
              pedidos, ubicación, segmento RFM y más.
            </p>
          </div>
        ) : results.length === 0 ? (
          <div className="card p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              Sin resultados
            </h3>
            <p className="text-slate-500">
              No se encontraron clientes con los filtros aplicados.
              Prueba a ajustar los criterios de búsqueda.
            </p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {results.length} clientes encontrados
              </span>
              <button
                onClick={handleExportCSV}
                className="btn btn-secondary btn-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Empresa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      País
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Grupo
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Pedidos
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Facturación
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Ticket Medio
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Última Compra
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Segmento
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {results.map((customer) => (
                    <ResultRow
                      key={customer.email}
                      customer={customer}
                      onClick={() => setSelectedCustomerEmail(customer.email)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomerEmail && (
        <CustomerDetailModal
          email={selectedCustomerEmail}
          onClose={() => setSelectedCustomerEmail(null)}
        />
      )}
    </div>
  )
}
